/** Chat message in OpenAI-compatible format. */
export interface Message {
  role: string;
  content: string;
}

/** Usage field that the client requests the TEE to reveal (selective disclosure). */
export type DiscloseField =
  | 'prompt_tokens'
  | 'cached_tokens'
  | 'completion_tokens'
  | 'reasoning_tokens'
  | 'total_tokens'
  | 'proxy_start_time'
  | 'proxy_end_time'
  | 'worker_start_time'
  | 'worker_end_time'
  | 'model'
  | 'worker_debug'
  | 'proxy_debug';

/** Disclose field constants for convenient access. */
export const DiscloseFields = {
  PromptTokens: 'prompt_tokens',
  CachedTokens: 'cached_tokens',
  CompletionTokens: 'completion_tokens',
  ReasoningTokens: 'reasoning_tokens',
  TotalTokens: 'total_tokens',
  ProxyStartTime: 'proxy_start_time',
  ProxyEndTime: 'proxy_end_time',
  WorkerStartTime: 'worker_start_time',
  WorkerEndTime: 'worker_end_time',
  Model: 'model',
  WorkerDebug: 'worker_debug',
  ProxyDebug: 'proxy_debug',
} as const satisfies Record<string, DiscloseField>;

/** Worker chat-template kwargs. Mirrors vLLM/sglang convention. */
export interface ChatTemplateKwargs {
  enableThinking?: boolean; // wire: enable_thinking
}

/** Inference request payload. */
export interface InferenceRequest {
  model: string;
  messages: Message[];
  maxTokens?: number;
  stream?: boolean;
  disclose?: DiscloseField[];
  chatTemplateKwargs?: ChatTemplateKwargs;
}

/** TEE attestation for usage data. */
export interface Attestation {
  usageHash: string;
  signature: string;
  sessionId: string;
}

/** Token usage and timing statistics from a completed inference. */
export interface Usage {
  promptTokens: number;
  cachedTokens: number;
  completionTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  proxyStartTime?: number;
  proxyEndTime?: number;
  workerStartTime?: number;
  workerEndTime?: number;
  model: string;
  workerDebug?: string;
  proxyDebug?: string;
  attestation?: Attestation;
  verified: boolean;
}

/** A single streaming response chunk. */
export interface Chunk {
  content: string;
  reasoningContent: string;
  /** @internal Tool-call passthrough; stable public typed surface lands in a later phase. */
  toolCalls?: unknown[];
}

/** An available inference model. */
export interface Model {
  id: string;
  object: string;
  ownedBy: string;
  activeWorkers: number;
  coefficientMin: number;
  coefficientBucket50: number;
  coefficientMax: number;
}

/** Client configuration options. */
export interface CocoonClientOptions {
  apiKey?: string;
  modelsPath?: string;
  streamPath?: string;
  attestationPolicy?: AttestationPolicy | null;
  /** Custom WebSocket constructor (for Node.js or custom environments). */
  WebSocket?: unknown;
  /** AbortSignal for cancellation. */
  signal?: AbortSignal;
}

/** Attestation verification policy. */
export interface AttestationPolicy {
  allowedCocoonProxyImageHashes: string[];
}

// ---- Wire format types (snake_case JSON) ----

/** @internal */
export interface WireUsage {
  prompt_tokens: number;
  cached_tokens: number;
  completion_tokens: number;
  reasoning_tokens: number;
  total_tokens: number;
  proxy_start_time?: number;
  proxy_end_time?: number;
  worker_start_time?: number;
  worker_end_time?: number;
  model: string;
  worker_debug?: string;
  proxy_debug?: string;
}

/** @internal */
export interface WireAttestation {
  usage_hash: string;
  signature: string;
  session_id: string;
}

/** @internal */
export interface WireModel {
  id: string;
  object: string;
  owned_by: string;
  active_workers: number;
  coefficient_min: number;
  coefficient_bucket50: number;
  coefficient_max: number;
}

/** Convert wire usage (snake_case) to SDK usage (camelCase). */
export function fromWireUsage(w: WireUsage): Usage {
  return {
    promptTokens: w.prompt_tokens,
    cachedTokens: w.cached_tokens,
    completionTokens: w.completion_tokens,
    reasoningTokens: w.reasoning_tokens,
    totalTokens: w.total_tokens,
    proxyStartTime: w.proxy_start_time,
    proxyEndTime: w.proxy_end_time,
    workerStartTime: w.worker_start_time,
    workerEndTime: w.worker_end_time,
    model: w.model,
    workerDebug: w.worker_debug,
    proxyDebug: w.proxy_debug,
    verified: false,
  };
}

/** Convert wire attestation to SDK attestation. */
export function fromWireAttestation(w: WireAttestation): Attestation {
  return {
    usageHash: w.usage_hash,
    signature: w.signature,
    sessionId: w.session_id,
  };
}

/** Convert wire model to SDK model. */
export function fromWireModel(w: WireModel): Model {
  return {
    id: w.id,
    object: w.object,
    ownedBy: w.owned_by,
    activeWorkers: w.active_workers,
    coefficientMin: w.coefficient_min,
    coefficientBucket50: w.coefficient_bucket50,
    coefficientMax: w.coefficient_max,
  };
}
